import { useEffect } from 'react';
import { AxiosResponse, AxiosError } from 'axios';
import api from '../services/api';
import { useCreditError } from '../components/credits/CreditErrorProvider';
import { CreditErrorInfo } from '../components/credits/CreditErrorModal';

export const useCreditErrorInterceptor = () => {
  const { showCreditError } = useCreditError();

  useEffect(() => {
    // Add response interceptor for 402 errors
    const interceptor = api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        // Handle 402 Payment Required errors
        if (error.response?.status === 402) {
          const errorData = error.response.data as any;
          console.log('402 Error Data:', errorData); // Debug log
          
          // Handle both nested and direct error structures
          const errorDetail = errorData?.detail || errorData;
          
          // Check if this is a structured credit error response
          if (errorDetail && (errorDetail.error_code === 'INSUFFICIENT_CREDITS' || errorData?.error_code === 'INSUFFICIENT_CREDITS')) {
            const creditErrorInfo: CreditErrorInfo = {
              operation: errorDetail.operation || errorData.operation || 'unknown',
              operation_display_name: errorDetail.operation_display_name || errorData.operation_display_name || 'Unknown Operation',
              credits_needed: errorDetail.credits_needed || errorData.credits_needed || 0,
              credits_available: errorDetail.credits_available || errorData.credits_available || 0,
              upgrade_suggestion: errorDetail.upgrade_suggestion || errorData.upgrade_suggestion || {
                suggested_tier: 'premium',
                message: 'Upgrade to Premium (₪39/month) for 400 credits monthly',
                benefits: '400 monthly credits for all features - lectures, documents, exams, and chat'
              }
            };
            
            // Show the credit error modal
            showCreditError(creditErrorInfo);
            
            // Create a custom error to prevent other error handlers from showing
            const customError = new Error('CREDIT_ERROR_HANDLED');
            customError.name = 'CreditErrorHandled';
            return Promise.reject(customError);
          }
        }
        
        return Promise.reject(error);
      }
    );

    // Cleanup function to remove interceptor
    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [showCreditError]);
};